import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from './api';
import {
  DashboardStatsModel,
  RecentPaymentModel,
  OverdueContractModel,
} from './dashboard';

// ── Thunks — call real API, surface errors for debugging ──

export const fetchDashboardStats = createAsyncThunk(
  'dashboard/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const result = await api.getDashboardStats();
      if (result.succeeded && result.value) {
        if (import.meta.env.DEV) {
          console.debug('[Dashboard] Stats fetched:', result.value);
        }
        return result.value;
      }
      const errorMsg = result.errors?.[0] ?? 'Failed to load dashboard stats';
      if (import.meta.env.DEV) {
        console.warn('[Dashboard] Stats API returned failure:', result);
      }
      return rejectWithValue(errorMsg);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load dashboard stats';
      if (import.meta.env.DEV) {
        console.error('[Dashboard] Stats fetch error:', err);
      }
      return rejectWithValue(message);
    }
  }
);

export const fetchRecentPayments = createAsyncThunk(
  'dashboard/fetchRecentPayments',
  async (_, { rejectWithValue }) => {
    try {
      const result = await api.getRecentPayments();
      if (result.succeeded && result.value) return result.value;
      return rejectWithValue(
        result.errors?.[0] ?? 'Failed to load recent payments'
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load recent payments';
      if (import.meta.env.DEV)
        console.error('[Dashboard] Recent payments error:', err);
      return rejectWithValue(message);
    }
  }
);

export const fetchOverdueContracts = createAsyncThunk(
  'dashboard/fetchOverdueContracts',
  async (_, { rejectWithValue }) => {
    try {
      const result = await api.getOverdueContracts();
      if (result.succeeded && result.value) return result.value;
      return rejectWithValue(
        result.errors?.[0] ?? 'Failed to load overdue contracts'
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load overdue contracts';
      if (import.meta.env.DEV)
        console.error('[Dashboard] Overdue contracts error:', err);
      return rejectWithValue(message);
    }
  }
);

// ── Slice ────────────────────────────────────────────────────
//
// Each section tracks its own loading/error so one slow or failing
// endpoint can no longer blank the entire dashboard. The global
// `error`/`isLoading` are reserved for the core stats KPIs — that is
// the only data whose absence makes the dashboard genuinely unusable.

interface DashboardState {
  stats: DashboardStatsModel | null;
  recentPayments: RecentPaymentModel[];
  overdueContracts: OverdueContractModel[];
  isLoading: boolean;
  error: string | null;
  recentPaymentsLoading: boolean;
  recentPaymentsError: string | null;
  overdueLoading: boolean;
  overdueError: string | null;
}

const initialState: DashboardState = {
  stats: null,
  recentPayments: [],
  overdueContracts: [],
  isLoading: false,
  error: null,
  recentPaymentsLoading: false,
  recentPaymentsError: null,
  overdueLoading: false,
  overdueError: null,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.recentPaymentsError = null;
      state.overdueError = null;
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // ── Core stats: drives the global loading/error state ──
      .addCase(fetchDashboardStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.isLoading = false;
        state.stats = null;
        state.error = (action.payload as string) ?? 'Failed to load dashboard';
      })
      // ── Recent payments: section-scoped state ──
      .addCase(fetchRecentPayments.pending, (state) => {
        state.recentPaymentsLoading = true;
        state.recentPaymentsError = null;
      })
      .addCase(fetchRecentPayments.fulfilled, (state, action) => {
        state.recentPaymentsLoading = false;
        state.recentPayments = action.payload;
      })
      .addCase(fetchRecentPayments.rejected, (state, action) => {
        state.recentPaymentsLoading = false;
        state.recentPayments = [];
        state.recentPaymentsError =
          (action.payload as string) ?? 'Failed to load recent payments';
      })
      // ── Overdue contracts: section-scoped state ──
      .addCase(fetchOverdueContracts.pending, (state) => {
        state.overdueLoading = true;
        state.overdueError = null;
      })
      .addCase(fetchOverdueContracts.fulfilled, (state, action) => {
        state.overdueLoading = false;
        state.overdueContracts = action.payload;
      })
      .addCase(fetchOverdueContracts.rejected, (state, action) => {
        state.overdueLoading = false;
        state.overdueContracts = [];
        state.overdueError =
          (action.payload as string) ?? 'Failed to load overdue contracts';
      });
  },
});

export const { clearError, reset } = dashboardSlice.actions;
export default dashboardSlice.reducer;
